import React, { useState, useCallback } from 'react'
import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'

import Topics from './Topics';
import Challenges from './Challenges';
import HeroSection from './HeroSection'
import ScoreCard from './ScoreCard';
import PreparationList from './PreparationList'
import RecentAttempts from './RecentAttempts'
import { AssessmentEntryPoint, CaseMasterScreens } from './constant';
import CreatePersonalizedChallenge from './CreatePersonalizedChallenge';
import FeedbackPendingCard from './FeedbackPendingCard';
import { getCaseMasterProblems, getRecentCaseMasterAttempts } from 'services';
import {
  Session, getCaseMasterPlan, isB2cCaseMasterUser, hasFullCaseMasterAccess,
  CaseMasterPlans,
} from 'utils';
import { CaseMasterAccessProvider } from './CaseMasterAccessContext';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    width: '100%',
  }
}));

const CaseMaster = () => {
  const classes = useStyles();

  const [activeScreen, setActiveScreen] = useState(CaseMasterScreens.HOME);
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);

  const [hasPendingAttempt, setHasPendingAttempt] = useState(false);
  const [pendingAttemptCardShown, setPendingAttemptCardShown] = useState(false);

  const [selectedCard, setSelectedCard] = useState({});
  const [entryPoint, setEntryPoint] = useState({ data: 'case_master' });

  // DEBUG(casemaster-plan): temporary logging to inspect the B2C plan inputs and
  // (for free users) the flagged free case + attempt-spent state. Remove before merge.
  React.useEffect(() => {
    (async () => {
      const metadata = Session.getUserMetadata();
      console.log('[CaseMaster][DEBUG] metadata:', metadata);
      console.log('[CaseMaster][DEBUG] isB2cCaseMasterUser:', isB2cCaseMasterUser());
      console.log('[CaseMaster][DEBUG] casemasterPlanId:', metadata?.casemasterPlanId, '→ resolved:', getCaseMasterPlan());
      console.log('[CaseMaster][DEBUG] validTill:', metadata?.validTill);
      console.log('[CaseMaster][DEBUG] hasFullCaseMasterAccess:', hasFullCaseMasterAccess());

      if (!isB2cCaseMasterUser() || getCaseMasterPlan() !== CaseMasterPlans.FREE) return;

      try {
        const [problemsRes, attempts] = await Promise.all([
          getCaseMasterProblems({ isFreeCase: true, isActive: true }),
          getRecentCaseMasterAttempts({ user: Session.userId }),
        ]);

        const freeCase = problemsRes?.casemasterProblems?.[0] || null;
        const freeAttemptUsed = Array.isArray(attempts) &&
          attempts.some((a) => a?.status === 'feedback_generated');

        console.log('[CaseMaster][DEBUG] flagged free case:',
          freeCase ? { _id: freeCase._id, title: freeCase.title, topic: freeCase.topic } : '⚠️ none returned');
        console.log('[CaseMaster][DEBUG] freeAttemptUsed:', freeAttemptUsed);
      } catch (err) {
        console.error('[CaseMaster][DEBUG] ❌ free-plan lookup failed:', err?.message);
      }
    })();
  }, []);

  const handleAttemptStatusCheck = useCallback(({ hasPending }) => {
    if (hasPending && !pendingAttemptCardShown) {
      setHasPendingAttempt(true);
    }
  }, [pendingAttemptCardShown]);

  const handleClosePendingCard = useCallback(() => {
    setHasPendingAttempt(false);
    setPendingAttemptCardShown(true);
  }, []);

  const handleCardClick = useCallback((data) => {
    setSelectedCard(data);
    if(data?.tag){
      setEntryPoint({
        data: AssessmentEntryPoint.GENERAL_CASE_STUDY,
        tagId: data.tag._id
      });
      setSelectedSubTopic(data.tag);
      setActiveScreen(CaseMasterScreens.CHALLENGES);
    } else {
      setEntryPoint({data: AssessmentEntryPoint.PERSONALIZED, tagId: data._id});
      setActiveScreen(CaseMasterScreens.TOPICS)
    }
  }, []);

  const handleHeroScreenChange = useCallback((e) => {
    setSelectedCard({});
    setActiveScreen(e);
  }, []);

  const handleTopicsSelect = useCallback((e) => {
    setSelectedSubTopic(e);
    setActiveScreen(CaseMasterScreens.CHALLENGES);
    setEntryPoint({
      data: AssessmentEntryPoint.GENERAL_CASE_STUDY,
      tagId: e._id
    });
  }, []);

  const handleTopicsBack = useCallback(() => {
    setActiveScreen(CaseMasterScreens.HOME);
  }, []);

  const handleChallengesBack = useCallback(() => {
    setSelectedSubTopic(null);
    setActiveScreen(CaseMasterScreens.TOPICS);
  }, []);

  const handlePersonalizedBack = useCallback(() => {
    setActiveScreen(CaseMasterScreens.HOME);
  }, []);

  return (
    <CaseMasterAccessProvider>
    <Box className={classes.root}>
    {
      activeScreen === CaseMasterScreens.HOME &&
        <>
          {/* {
            hasPendingAttempt && <FeedbackPendingCard setPendingAttempt={handleClosePendingCard}/>
          } */}
          {/* <ScoreCard /> */}
          <HeroSection setActiveScreen={handleHeroScreenChange} />
          <PreparationList onClick={handleCardClick} />
          <RecentAttempts onAttemptStatusCheck={handleAttemptStatusCheck}/>
        </>
    }
    {
      activeScreen === CaseMasterScreens.TOPICS &&
        <Topics
          setSelected={handleTopicsSelect}
          handleBack={handleTopicsBack}
        />
    }
    {
      activeScreen === CaseMasterScreens.CHALLENGES &&
        <Challenges
          entryPoint={entryPoint}
          subTopic={selectedSubTopic}
          handleBack={handleChallengesBack}
        />
    }
    {
      activeScreen === CaseMasterScreens.PERSONALIZED &&
        <CreatePersonalizedChallenge handleBack={handlePersonalizedBack} />
    }
    </Box>
    </CaseMasterAccessProvider>
  )
}

export default CaseMaster;
